/* eslint-disable max-len */
import React, { useEffect, useCallback } from 'react';

import PropTypes from 'prop-types';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useTranslator } from '@jutro/locale';
import _ from 'lodash';
import appConfig from 'app-config';
import { platformMessages as commonMessages } from 'ml-components-platform-react';
import defaultMetadata from './MLNameDetailsComponent.default.metadata.json5';
import splitMetadata from './MLNameDetailsComponent.split.metadata.json5';
import styles from './MLNameDetailsComponent.module.scss';
import messages from './MLNameDetailsComponent.messages';

// ML: NameDetails.js

function MLNameDetailsComponent(props) {
    const translator = useTranslator();

    const {
        id,
        onValidate,
        showValidationErrors,
        //
        personVM, // PersonDTLML
        onModelChange,
        layout,
        isBilling,
        changeNameLabel,
        showLabels,
        submissionVM,
        disableNameFields = false
    } = props;

    const {
        onValidate: setComponentValidation,
        isComponentValid
    } = useValidation(id);
    const QUOTED_SUBMISSIONS_KEY = 'quotedSubmissionNumbers';

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    const isQnB = appConfig.portalCode === 'QB';

    /* 
     * If the component is no longer visible in the modal,
     * set it's validation to true to avoid FormModalPopover's isComponentValid from getting stuck with an invalid validation that no longer matters
    */
    useEffect(() => {
        return () => {
            if (onValidate) {
                onValidate(true, id);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const isQuoted = (quoteNumber) => {
        if (!quoteNumber) return false;
        const list = JSON.parse(localStorage.getItem(QUOTED_SUBMISSIONS_KEY) || '[]');
        return list.includes(quoteNumber);
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    const resolveUiProps = () => {
        switch (layout) {
            case 'ML-Name-Default':
                return { content: defaultMetadata.componentContent };
            case 'ML-Split':
                return { content: splitMetadata.componentContent };
            default:
                return { content: defaultMetadata.componentContent };
        }
    };

    const validateNoAlphaNumericCharacters = (name) => {
        return name && !/^[^0-9]+$/.test(name);
    };

    const getNameValdiaitonMessage = (name) => {
        if (name && name?.value && validateNoAlphaNumericCharacters(name?.value)) {
            return [messages.noAlphaNumeric_ML];
        }

        return name?.aspects?.validationMessages;
    };

    const getFirstNameLabel = useCallback(() => {
        if (_.isUndefined(changeNameLabel)) {
            return layout === 'ML-Split' ? translator(commonMessages.mlFirstName) : translator(commonMessages.mlName)
        }
        return changeNameLabel;
    }, [changeNameLabel, layout, translator]);

    const checkIsDriverPNI = () => {
        if (_.get(submissionVM, 'baseData.primaryNamedInsured_ML.publicID.value') === _.get(personVM, 'publicID.value')) {
            return true;
        }
        return false;
    };

    const overrides = {
        firstName: {
            label: isQnB ? translator(commonMessages.mlFirstName) : getFirstNameLabel(),
            validationMessages: getNameValdiaitonMessage(_.get(personVM, 'firstName')),
            disabled: disableNameFields || (isQnB && (submissionVM?.value.isQuoted() || isQuoted(submissionVM?.quoteID?.value) || !_.isEmpty(_.get(submissionVM, 'quoteData.paymentQuotes_ML.value'))) && checkIsDriverPNI())
        },
        middleName: {
            path: _.get(personVM, 'middleName') ? 'middleName' : 'middleName_ML',
            label: translator(commonMessages.mlMiddleName),
            hideLabel: true,
            validationMessages: getNameValdiaitonMessage(_.get(personVM, 'middleName') ?? _.get(personVM, 'middleName_ML')),
            disabled: disableNameFields || (isQnB && (submissionVM?.value.isQuoted() || isQuoted(submissionVM?.quoteID?.value) || !_.isEmpty(_.get(submissionVM, 'quoteData.paymentQuotes_ML.value'))) && checkIsDriverPNI()),
            visible: !isQnB
        },
        lastName: {
            label: translator(commonMessages.mlLastName),
            hideLabel: !isQnB,
            className: isQnB ? 'name vertical-spacing-5' : 'name no-label-top-spacing vertical-spacing-5',
            validationMessages: getNameValdiaitonMessage(_.get(personVM, 'lastName')),
            disabled: disableNameFields || (isQnB && (submissionVM?.value.isQuoted() || isQuoted(submissionVM?.quoteID?.value) || !_.isEmpty(_.get(submissionVM, 'quoteData.paymentQuotes_ML.value'))) && checkIsDriverPNI())
        },
        suffix: {
            path: _.get(personVM, 'suffix') ? 'suffix' : 'suffix_ML',
            disabled: disableNameFields || (isQnB && (submissionVM?.value.isQuoted() || isQuoted(submissionVM?.quoteID?.value) || !_.isEmpty(_.get(submissionVM, 'quoteData.paymentQuotes_ML.value'))) && checkIsDriverPNI()),
            visible: !isQnB
        },
        MLNameDetailsComponentContainer: {
            className: `${isQnB ? 'nameRow qnbWidthReset' : 'nameRow'}`
        }
    };

    return (
        <ViewModelForm
            uiProps={resolveUiProps().content}
            model={personVM}
            overrideProps={overrides}
            onModelChange={onModelChange}
            onValidationChange={setComponentValidation}
            showErrors={showValidationErrors}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

MLNameDetailsComponent.propTypes = {
    id: PropTypes.string,
    onValidate: PropTypes.func,
    showValidationErrors: PropTypes.bool,
    //
    personVM: PropTypes.shape({}).isRequired,
    onModelChange: PropTypes.func.isRequired,
    layout: PropTypes.string,
    isBilling: PropTypes.bool,
    changeNameLabel: PropTypes.string,
    showLabels: PropTypes.bool
};

MLNameDetailsComponent.defaultProps = {
    id: undefined,
    onValidate: undefined,
    showValidationErrors: undefined,
    layout: '',
    isBilling: false,
    changeNameLabel: undefined,
    showLabels: false
};

export default MLNameDetailsComponent;
